import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrencyData } from '../../features/CurrencySlice/CurrencySlice';
import Loading from '../Loading';
import CurrencyDropdown from './CurrencyDropdown';
import { DonateButton } from '../../Buttons/Buttons';
import { IconAddMenu, IconBank, IconCamel, IconCashMoney, IconCompanyMoney, IconCow, IconCrop, IconDebtMoney, IconGoat, IconGold, IconInvestment, IconSheep } from '../../Icons/Icon';

// -------------------- Helpers --------------------
const parseIntSafe = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const s = String(value).trim();
  const cleaned = s.replace(/[^0-9-]/g, '');
  const n = parseInt(cleaned || '0', 10);
  return Number.isNaN(n) ? 0 : n;
};

const parseFloatSafe = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const s = String(value).trim();
  const cleaned = s.replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned || '0');
  return Number.isNaN(n) ? 0 : n;
};

const formatCurrency = (value, symbol = '', locale = undefined) => {
  const n = Number(value) || 0;
  try {
    return `${symbol} ${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n)}`.trim();
  } catch (e) {
    return `${symbol} ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim();
  }
};

// -------------------- Zakat rules --------------------
export function zakatSheepRaw(n) {
  n = Math.floor(parseIntSafe(n));
  if (n <= 0) return 0;
  if (n <= 39) return 1;
  if (n <= 120) return 2;
  if (n <= 200) return 3;
  if (n <= 399) return 4;
  return 4 + Math.floor((n - 399) / 100);
}

export function zakatCowsRaw(n, t = (s) => s) {
  n = Math.floor(parseIntSafe(n));

  if (n < 30) return { cows: 0, calves: 0, text: '0' };
  if (n > 200) return { cows: 0, calves: 0, text: t('MinistryOfReligiousAssembly') };

  const ranges = [
    { min: 30, max: 39, calves: 1, cows: 0, text: `1 ${t('oneYearOldCow')}` },
    { min: 40, max: 59, calves: 0, cows: 1, text: `1 ${t('twoYearOldCow')}` },
    { min: 60, max: 69, calves: 2, cows: 0, text: `2 ${t('oneYearOldCow')}` },
    { min: 70, max: 79, calves: 1, cows: 1, text: `1 ${t('oneYearOldCow')} ${t('and')} 1 ${t('twoYearOldCow')}` },
    { min: 80, max: 89, calves: 0, cows: 2, text: `2 ${t('twoYearOldCow')}` },
    { min: 90, max: 99, calves: 3, cows: 0, text: `3 ${t('oneYearOldCow')}` },
    { min: 100, max: 109, calves: 2, cows: 1, text: `2 ${t('oneYearOldCow')} ${t('and')} 1 ${t('twoYearOldCow')}` },
    { min: 110, max: 119, calves: 1, cows: 2, text: `1 ${t('oneYearOldCow')} ${t('and')} 2 ${t('twoYearOldCow')}` },
    { min: 120, max: 129, calves: 4, cows: 0, text: `4 ${t('oneYearOldCow')} ${t('or')} 3 ${t('twoYearOldCow')}` },
    { min: 130, max: 139, calves: 3, cows: 1, text: `3 ${t('oneYearOldCow')} ${t('and')} 1 ${t('twoYearOldCow')}` },
    { min: 140, max: 149, calves: 2, cows: 2, text: `2 ${t('oneYearOldCow')} ${t('and')} 2 ${t('twoYearOldCow')}` },
    { min: 150, max: 159, calves: 1, cows: 3, text: `1 ${t('oneYearOldCow')} ${t('and')} 3 ${t('twoYearOldCow')}` },
    { min: 160, max: 169, calves: 5, cows: 0, text: `5 ${t('oneYearOldCow')} ${t('or')} 4 ${t('twoYearOldCow')}` },
    { min: 170, max: 179, calves: 4, cows: 1, text: `4 ${t('oneYearOldCow')} ${t('and')} 1 ${t('twoYearOldCow')}` },
    { min: 180, max: 189, calves: 3, cows: 2, text: `3 ${t('oneYearOldCow')} ${t('and')} 2 ${t('twoYearOldCow')}` },
    { min: 190, max: 199, calves: 2, cows: 3, text: `2 ${t('oneYearOldCow')} ${t('and')} 3 ${t('twoYearOldCow')}` },
    { min: 200, max: 200, calves: 0, cows: 5, text: `5 ${t('twoYearOldCow')}` },
  ];

  const match = ranges.find((r) => n >= r.min && n <= r.max);
  if (match) return { cows: match.cows, calves: match.calves, text: match.text };

  // fallback: greedy search to minimize leftover using 40/30 decomposition
  let best = { cows: 0, calves: 0, leftover: n };
  const maxTwo = Math.floor(n / 40);
  for (let two = 0; two <= maxTwo; two++) {
    const remaining = n - two * 40;
    const one = Math.floor(remaining / 30);
    const leftover = remaining - one * 30;
    if (leftover < best.leftover) {
      best = { cows: two, calves: one, leftover };
      if (leftover === 0) break;
    }

  }

  const text = `${best.cows} ${t('twoYearOldCow')} ${t('and')} ${best.calves} ${t('oneYearOldCow')}${best.leftover ? ` (+ leftover ${best.leftover})` : ''}`;
  return { cows: best.cows, calves: best.calves, text };
}

export function zakatCamelsRaw(n, t = (s) => s) {
  n = Math.floor(parseIntSafe(n));
  if (n < 5) return { mer: 0, shoti: {}, text: '0' };
  if (n > 200) return { mer: 0, shoti: {}, text: t('MinistryOfReligiousAssembly') };

  const ranges = [
    { min: 5, max: 9, shoti: { sheep6m: 1 }, text: `(1) ${t('sheep')} (${t('sixMonths')})` },
    { min: 10, max: 14, shoti: { sheep6m: 2 }, text: `(2) ${t('sheep')} (${t('sixMonths')})` },
    { min: 15, max: 19, shoti: { sheep6m: 3 }, text: `(3) ${t('sheep')} (${t('sixMonths')})` },
    { min: 20, max: 24, shoti: { sheep6m: 4 }, text: `(4) ${t('sheep')} (${t('sixMonths')})` },

    { min: 25, max: 35, shoti: { '1y': 1 }, text: `(1) ${t('oneCam1Year')}` },
    { min: 36, max: 45, shoti: { '2y': 1 }, text: `(1) ${t('oneCam2Years')}` },
    { min: 46, max: 60, shoti: { '3y': 1 }, text: `(1) ${t('oneCam3Years')}` },
    { min: 61, max: 75, shoti: { '4y': 1 }, text: `(1) ${t('cam4Years')}` },
    { min: 76, max: 90, shoti: { '2y': 2 }, text: `(2) ${t('twoYearOldCam')}` },
    { min: 91, max: 120, shoti: { '3y': 2 }, text: `(2) ${t('threeYearOldCam')}` },
    { min: 121, max: 129, shoti: { '2y': 3 }, text: `(3) ${t('twoYearOldCam')}` },
    { min: 130, max: 139, shoti: { '3y': 1, '2y': 2 }, text: `(1) ${t('threeYearOldCam')} ${t('and')} (2) ${t('twoYearOldCam')}` },
    { min: 140, max: 149, shoti: { '3y': 2, '2y': 1 }, text: `(2) ${t('threeYearOldCam')} ${t('and')} (1) ${t('twoYearOldCam')}` },
    { min: 150, max: 159, shoti: { '3y': 3 }, text: `(3) ${t('threeYearOldCam')}` },
    { min: 160, max: 169, shoti: { '2y': 4 }, text: `(4) ${t('twoYearOldCam')}` },
    { min: 170, max: 179, shoti: { '3y': 1, '2y': 3 }, text: `(1) ${t('threeYearOldCam')} ${t('and')} (3) ${t('twoYearOldCam')}` },
    { min: 180, max: 189, shoti: { '3y': 2, '2y': 2 }, text: `(2) ${t('threeYearOldCam')} ${t('and')} (2) ${t('twoYearOldCam')}` },
    { min: 190, max: 199, shoti: { '3y': 3, '2y': 1 }, text: `(3) ${t('threeYearOldCam')} ${t('and')} (1) ${t('twoYearOldCam')}` },
    { min: 200, max: 200, shoti: { '2y': 5, '3y_alternative': 4 }, text: `(5) ${t('twoYearOldCam')} ${t('or')} (4) ${t('threeYearOldCam')}` },
  ];

  const match = ranges.find((r) => n >= r.min && n <= r.max);
  if (match) return { mer: 0, shoti: match.shoti, text: match.text };

  const mer = Math.floor(n / 5);
  const remainder = n % 5;
  if (remainder === 0) return { mer, shoti: {}, text: `${mer} x ${t('merGroup') || '5-camels group (mer)'}` };
  return { mer, shoti: {}, remainder, text: `${mer} x ${t('merGroup')} + ${remainder} ${t('camel')}` };
}

// -------------------- AnimalZakatCard (user provided) --------------------
export function AnimalZakatCard({ animalZakatResult = {}, t = (s) => s }) {
  const sheepCount = animalZakatResult?.small?.sheep?.zakatHeads ?? 0;
  const calves = animalZakatResult?.cows?.calves ?? 0;
  const cows = animalZakatResult?.cows?.cows ?? 0;
  const cowText = animalZakatResult?.cows?.text ?? '0';
  const camelsText = animalZakatResult?.camels?.text ?? '0';
  const cropsKg = animalZakatResult?.crops?.zakatKg ?? 0;

  // Check if any zakat result has value greater than 0
  const hasZakatData = sheepCount > 0 || calves > 0 || cows > 0 ||
    (camelsText !== '0' && camelsText !== '') || (cowText !== '0' && cowText !== '') || cropsKg > 0;

  console.log(cowText)
  if (!hasZakatData) {
    return null;
  }

  return (
    <section className="min-w-full mx-auto mt-6 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 p-4 sm:p-6 rounded-2xl border border-emerald-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
      <header className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center gap-3">

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-emerald-800">{t('zakatAmountAnimals&Crops') || 'Zakat Calculation'}</h2>
          </div>
        </div>

      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Small animals card - Only show if has data */}
        {(sheepCount > 0) && (
          <div className="group bg-white/80 backdrop-blur-sm p-5 rounded-xl border border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-emerald-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">

                <div>
                  <div className="font-semibold text-emerald-800 text-lg">{t('sheep') + ' & ' + t('goat') || 'Small animals'}</div>

                </div>
              </div>
            </div>

            <div className="flex gap-4 justify-between">
              <div className="flex-1 p-4 bg-gradient-to-br from-emerald-50 to-white rounded-lg text-center border border-emerald-100 group-hover:from-emerald-100 transition-colors">
                
                <div className="text-lg  font-bold text-emerald-900 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent" aria-live="polite">
                  {sheepCount} {t('sheep') + '/' + t('goat') || 'Sheep'}
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Cows card - Only show if has data */}

        {(cowText !== '0') && (
          <div className="group bg-white/80 backdrop-blur-sm p-5 rounded-xl border border-amber-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-amber-200">
            <div className="flex items-center justify-between mb-4">



              <div className="font-semibold text-amber-800 text-center text-lg">{t('cows') || 'Cows'}</div>


            </div>
            {

              <div className="flex-1 p-4 bg-gradient-to-br from-amber-50 to-white rounded-lg text-center border border-amber-100 group-hover:from-amber-100 transition-colors">
                
                <div className="text-lg  font-bold text-amber-900 bg-gradient-to-r from-amber-600 to-amber-600 bg-clip-text text-transparent" aria-live="polite">
                  {cowText}
                </div>

              </div>
            }

          </div>
        )}

        {(camelsText !== '0') && (
          <div className="group bg-white/80 backdrop-blur-sm p-5 rounded-xl border border-amber-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-amber-200">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-orange-800 text-lg">{t('camel') || 'Camels'}</div>
            </div>
            {

              <div className="flex-1 p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg text-center border border-orange-100 group-hover:from-orange-100 transition-colors">
                
                <div className="text-lg font-bold text-orange-900 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  {camelsText}
                </div>

              </div>
            }

          </div>
        )}

        {(cropsKg > 0) && (
          <div className="group bg-white/80 backdrop-blur-sm p-5 rounded-xl border border-amber-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-amber-200">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lime-800 text-lg">{t('crops') || 'Crops'}</div>
            </div>
            {

              <div className="flex-1 p-4 bg-gradient-to-br from-lime-50 to-white rounded-lg text-center border border-lime-100 group-hover:from-lime-100 transition-colors">
                
                <div className="text-lg font-bold text-lime-900 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                <div className="flex items-center gap-1 justify-center font-bold text-lime-900 bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent">
                  {cropsKg > 0 ? cropsKg.toFixed(2) : 0}

                  <div className="text-xs text-lime-500">KG</div>
                </div>
                
                </div>

              </div>
            }

          </div>
        )}
        {/* Crops card - Only show if has data */}
      
      </div>


    </section>
  );
}
// -------------------- ZakatPage component --------------------
export default function ZakatPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currencies = [], loading } = useSelector((state) => state.fetchCurrency || {});

  const [formData, setFormData] = useState({
    priceGoldOrSilver: '',
    cashMoney: '',
    debtMoney: '',
    companyMoney: '',
    bankMoney: '',
    investerMoney: '',
    anotherMoney: '',
    sheepCount: '',
    goatCount: '',
    cowCount: '',
    camelCount: '',
    cropWeight: '',
    irrigation: 'rain',
  });

  const [selectedCurrency, setSelectedCurrency] = useState(null);

  useEffect(() => {
    dispatch(fetchCurrencyData());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(currencies) && currencies.length) setSelectedCurrency((prev) => prev ?? currencies[0]);
  }, [currencies]);

  const currencySymbol = (selectedCurrency && selectedCurrency.symbol) || (currencies.find((c) => c.base === 1) || {}).symbol || '';
  const locale = (selectedCurrency && selectedCurrency.locale) || undefined;

  const monetaryTotal = useMemo(() => {
    return [
      formData.priceGoldOrSilver,
      formData.cashMoney,
      formData.debtMoney,
      formData.companyMoney,
      formData.bankMoney,
      formData.investerMoney,
      formData.anotherMoney,
    ].reduce((acc, val) => acc + parseFloatSafe(val), 0);
  }, [formData]);

  const animalZakatResult = useMemo(() => {
    try {
      const sheep = parseIntSafe(formData.sheepCount);
      const goat = parseIntSafe(formData.goatCount);
      const sheepZakat = zakatSheepRaw(sheep);
      const goatZakat = zakatSheepRaw(goat);
      const combined = sheep + goat;
      const combinedZakat = zakatSheepRaw(combined);

      const cowsCount = parseIntSafe(formData.cowCount);
      const cowsRes = cowsCount > 0 ? zakatCowsRaw(cowsCount, t) : null;
      const cowsHeads = cowsRes ? (parseIntSafe(cowsRes.cows) + parseIntSafe(cowsRes.calves)) : 0;

      const camelsCount = parseIntSafe(formData.camelCount);
      const camelsRes = camelsCount > 0 ? zakatCamelsRaw(camelsCount, t) : null;
      const camelsShotis = camelsRes ? Object.values(camelsRes.shoti || {}).reduce((a, b) => a + parseIntSafe(b), 0) : 0;

      const weight = parseFloatSafe(formData.cropWeight);
      const zakatKg = (() => {
        const w = weight;
        if (w < 653) return 0;
        switch (formData.irrigation) {
          case 'rain': return w * 0.1;
          case 'irrigated': return w * 0.05;
          case 'mixed': return w * 0.075;
          default: return 0;
        }
      })();

      return {
        small: {
          sheep: { count: sheep, zakatHeads: sheepZakat },
          goat: { count: goat, zakatHeads: goatZakat },
          combined: { total: combined, zakatHeads: combinedZakat },
        },
        cows: cowsRes ? { count: cowsCount, ...cowsRes, zakatHeads: cowsHeads } : null,
        camels: camelsRes ? { count: camelsCount, ...camelsRes, zakatShotis: camelsShotis } : null,
        crops: { weight, irrigation: formData.irrigation, zakatKg, description: zakatKg > 0 ? `${zakatKg.toFixed(2)} kg` : '' },
      };
    } catch (err) {
      return { error: String(err) };
    }
  }, [formData, t]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleIntegerChange = useCallback((e) => {
    const { name, value } = e.target;
    const cleaned = value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
  }, []);

  const handleIntegerBlur = useCallback((e) => {
    const { name } = e.target;
    setFormData((prev) => {
      const v = prev[name] ?? '';
      const sanitized = v.replace(/^0+(?=\d)/, '');
      return { ...prev, [name]: sanitized };
    });
  }, []);

  const handleCurrencyChange = useCallback((currency) => setSelectedCurrency(currency), []);

  if (loading) return <Loading />;

  return (
    <motion.div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 py-12  sm:px-6 lg:px-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }}>
      <motion.div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl p-4 md:p-8" initial="hidden" animate="visible">
        <CurrencyDropdown currencies={currencies} setSelectedCurrency={setSelectedCurrency} onChange={handleCurrencyChange} />

        <h2 className="text-3xl mt-4 font-bold text-emerald-800 mb-6 text-center">🌙 {t('zakat')}</h2>

        <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={(e) => e.preventDefault()} aria-label="zakat-form">
          {[
            { label: t('priceGoldOrSilver') || 'Price (Gold/Silver)', name: 'priceGoldOrSilver', icon: <IconGold width={'w-7'} height={'h-7'} /> },
            { label: t('cashMoney') || 'Cash', name: 'cashMoney', icon: <IconCashMoney width={'w-7'} height={'w-7'} /> },
            { label: t('debtMoney') || 'Debts', name: 'debtMoney', icon: <IconDebtMoney width={'32'} height={'32'} /> },
            { label: t('companyMoney') || 'Company', name: 'companyMoney', icon: <IconCompanyMoney width={'32'} height={'32'} /> },
            { label: t('bankMoney') || 'Bank', name: 'bankMoney', icon: <IconBank width={'w-8'} height={'w-8'} /> },
            { label: t('investerMoney') || 'Investments', name: 'investerMoney', icon: <IconInvestment width={'w-8'} height={'w-8'} /> },
            { label: t('anotherMoney') || 'Other', name: 'anotherMoney', icon: <IconAddMenu /> },

            { label: t('sheep') + " / " + t('goat') || 'Sheep (count)', name: 'sheepCount', icon: <IconSheep width={'w-8'} height={'h-8'} /> },
            // { label: t('goat') || 'Goat (count)', name: 'goatCount', icon: <IconGoat width={'w-8'} height={'h-8'} /> },
            { label: t('cow') || 'Cow (count)', name: 'cowCount', icon: <IconCow width={'w-8'} height={'h-8'} /> },
            { label: t('camel') || 'Camel (count)', name: 'camelCount', icon: <IconCamel width={'w-8'} height={'h-8'} /> },
            { label: t('corn') || 'Crops (kg)', name: 'cropWeight', icon: <IconCrop /> },
          ].map((field) => (
            <div key={field.name} className="space-y-2">
              <label htmlFor={field.name} className="flex items-center text-gray-700 font-medium">
                <span className="mr-2 text-xl">{field.icon}</span>
                {field.label}
              </label>

              {['sheepCount', 'goatCount', 'cowCount', 'camelCount'].includes(field.name) ? (
                <input
                  inputMode="numeric"
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleIntegerChange}
                  onBlur={handleIntegerBlur}
                  className="w-full p-4 border-2 outline-none border-emerald-100 rounded-xl focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 transition-all duration-200"
                  placeholder="0"
                  aria-label={field.label}
                />
              ) : field.name === 'cropWeight' ? (
                <input
                  inputMode="decimal"
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  className="w-full p-4 border-2 outline-none border-emerald-100 rounded-xl focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 transition-all duration-200"
                  placeholder="0.00"
                  aria-label={field.label}
                />
              ) : (
                <input
                  inputMode="decimal"
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  className="w-full p-4 border-2 outline-none border-emerald-100 rounded-xl focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 transition-all duration-200"
                  placeholder="0.00"
                  aria-label={field.label}
                />
              )}
            </div>
          ))}

          <div className="   rounded-xl">
            <label htmlFor="">{t('irrigation') || 'Irrigation'}</label>
            <div className="   items-center ">
              <select className="w-full p-4 border-2 outline-none border-emerald-100 rounded-xl focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 transition-all duration-200 mt-4"
                value={formData.irrigation} onChange={(e) => setFormData((p) => ({ ...p, irrigation: e.target.value }))} aria-label="Irrigation type">
                <option value="rain">{t('rain') || 'Rain (10%)'}</option>
                <option value="irrigated">{t('irrigated') || 'Irrigated (5%)'}</option>
                <option value="mixed">{t('mixed') || 'Mixed (7.5%)'}</option>
              </select>
            </div>
          </div>
        </form>

        <div className="mt-10 space-y-6 w-full">
          <AnimatePresence mode="wait">
            <motion.div key={monetaryTotal} className="bg-gradient-to-r w-full from-emerald-500 to-teal-500 p-2  md:p-6 rounded-xl shadow-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ type: 'spring', stiffness: 150 }}>
              <div className="flex flex-row justify-between items-center text-white gap-4">
                <div className="space-y-2 text-center md:text-left">
                  <h3 className="text-sm md:text-lg font-semibold">{t('totalMoney')}</h3>
                  <p className=" text-lg md:text-3xl font-bold">{formatCurrency(monetaryTotal, currencySymbol, locale)}</p>
                </div>

                <div className="text-center md:text-right">
                  <h3 className="text-sm md:text-lg font-semibold">{t('zakat')} (2.5%)</h3>
                  <p className="text-lg md:text-3xl font-bold">{formatCurrency(monetaryTotal * 0.025, currencySymbol, locale)}</p>
                </div>
              </div>

              <AnimalZakatCard animalZakatResult={animalZakatResult} t={t} />

              {/* {animalZakatResult?.cows?.text === 'call' || animalZakatResult?.camels?.text === 'call' ? (
                <div className="mt-4 text-yellow-100 font-medium">{t('contactScholar') || 'Please contact a scholar for large herds (>200).'}</div>
              ) : null} */}
            </motion.div>
          </AnimatePresence>

          <div className="">
            <DonateButton isFull={true} center={true} link={process.env.REACT_APP_DONATE_URL} disabled={!monetaryTotal} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

